import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, Send, X, Loader2, Mic, Square, Check, ShoppingBag } from "lucide-react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";

const CUSTOMER_SUGGESTED = [
  "Black Dior shoulder bag",
  "How long does delivery take?",
  "What size is M in Indian footwear?",
  "What payment methods do you accept?",
];
const ADMIN_SUGGESTED = [
  "How many orders today?",
  "Pending WhatsApp leads",
  "Revenue this week",
  "Top brand this month",
];

// Mirror of backend `_brand_slug`: lowercase, & → and, non-alphanum → '-'.
const slugifyBrand = (name) =>
  (name || "")
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

// Pick the best deep-link target for a shopper search result. Prefers the
// dedicated Brand Hub when a brand was detected so the customer only sees
// that brand's inventory — not a noisy keyword search.
const buildSearchTarget = (intent, fallbackText) => {
  if (intent?.brand) {
    const slug = slugifyBrand(intent.brand);
    if (slug) {
      const params = new URLSearchParams();
      if (intent.category) params.set("category", intent.category);
      if (intent.color) params.set("color", intent.color);
      if (intent.max_price) params.set("max", String(intent.max_price));
      const qs = params.toString();
      return qs ? `/brand/${slug}?${qs}` : `/brand/${slug}`;
    }
  }
  // No brand — build a focused search query from intent keywords + facets
  const parts = [];
  if (intent?.brand) parts.push(intent.brand);
  if (intent?.color) parts.push(intent.color);
  if (intent?.category) parts.push(intent.category);
  if (intent?.keywords && intent.keywords !== "product") parts.push(intent.keywords);
  const q = parts.join(" ").trim() || fallbackText || "";
  return `/search?q=${encodeURIComponent(q)}`;
};

const STORAGE_SID = "lx:shopper-session";

// Internal paths the AI agent may reference. Anything matching becomes a
// clickable in-app link inside the reply bubble. Order matters — longer paths
// first so prefixes don't swallow them.
const LINKABLE_PATHS = [
  "/source-on-demand",
  "/track-order",
  "/returns",
  "/reviews",
  "/contact",
  "/about",
  "/account",
];

// Split a reply string into [text, link, text, link, ...] segments by
// matching whole-word /path occurrences. We keep punctuation that follows the
// path outside the link.
const renderRichReply = (text, onNavigate) => {
  if (!text) return null;
  const escaped = LINKABLE_PATHS.map((p) => p.replace(/\//g, "\\/")).join("|");
  const re = new RegExp(`(${escaped})(?![A-Za-z0-9/_-])`, "g");
  const parts = [];
  let last = 0;
  let m;
  let idx = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const path = m[1];
    parts.push(
      <Link
        key={`l-${idx++}-${m.index}`}
        to={path}
        onClick={onNavigate}
        className="underline underline-offset-2 decoration-black/40 hover:decoration-black text-black font-medium"
        data-testid={`shopper-link-${path.replace(/\//g, "")}`}
      >
        {path}
      </Link>
    );
    last = m.index + path.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length ? parts : text;
};

// Action chips rendered under the bubble whenever the AI reply mentions a
// known internal path → gives the customer a single tap to deep-link.
const ACTION_LABELS = {
  "/track-order": "Track your order",
  "/returns": "Start a return",
  "/source-on-demand": "Source on Demand",
  "/reviews": "Read reviews",
  "/contact": "Contact us",
  "/account": "My account",
};
const extractActions = (text) => {
  if (!text) return [];
  const found = [];
  for (const p of LINKABLE_PATHS) {
    const re = new RegExp(`${p.replace(/\//g, "\\/")}(?![A-Za-z0-9/_-])`);
    if (re.test(text)) found.push(p);
  }
  return found;
};

/** Floating AI Personal Shopper. Opens to a chat panel. Each user message
 *  goes to /api/shopper/chat which returns natural-language reply + product
 *  cards (for shopping queries) OR just a reply (for FAQ/policy questions
 *  about sizing, delivery, payment, returns). Session id is persisted so
 *  multi-turn context is preserved on the server side. */
const PersonalShopper = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const cart = useCart();
  const isStaff = user?.role === "admin" || user?.role === "staff";
  const SUGGESTED = isStaff ? ADMIN_SUGGESTED : CUSTOMER_SUGGESTED;
  const [open, setOpen] = useState(false);
  const [addedKey, setAddedKey] = useState(null);
  const [messages, setMessages] = useState([
    {
      from: "bot",
      text: isStaff
        ? "Hi! Ask me about today's orders, pending leads, top brands, revenue — anything from your stats."
        : "Hi! I'm your Luxsova concierge. Ask me to find a piece, or about sizing, delivery and payment.",
      items: [],
    },
  ]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [sid, setSid] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_SID) || "";
    } catch {
      return "";
    }
  });
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  // External trigger: mobile bottom-nav "Atelier" button dispatches this event
  useEffect(() => {
    const handler = () => {
      setOpen(true);
      // Sticky prompt — pre-fill the input with a context-aware question
      // so the very first message feels tailored. PDP → product name;
      // PLP / brand / category → the current view name lifted from the
      // hero H1 the page is rendering.
      try {
        if (typeof window === "undefined") return;
        const path = window.location.pathname;
        setText((cur) => {
          if (cur) return cur; // don't clobber a partial draft
          const h1 = document.querySelector("h1")?.textContent?.trim();
          if (path.startsWith("/product/") && h1) {
            return `Tell me more about ${h1} — is it authentic and worth the price?`;
          }
          if ((path.startsWith("/category/") || path.startsWith("/collections") || path.startsWith("/brands/")) && h1) {
            return `Show me the top picks in ${h1} under my budget.`;
          }
          return cur;
        });
      } catch (_e) { /* SSR / private-mode noop */ }
    };
    window.addEventListener("luxsova:open-shopper", handler);
    return () => window.removeEventListener("luxsova:open-shopper", handler);
  }, []);

  const send = async (msg) => {
    const text = (msg ?? "").trim();
    if (!text || sending) return;
    setMessages((m) => [...m, { from: "user", text }]);
    setText("");
    setSending(true);
    try {
      const d = await api.shopperChat(text, sid || null);
      if (d.session_id && d.session_id !== sid) {
        setSid(d.session_id);
        try {
          localStorage.setItem(STORAGE_SID, d.session_id);
        } catch {
          /* ignore */
        }
      }
      setMessages((m) => [
        ...m,
        {
          from: "bot",
          text: d.reply || "Here are some pieces…",
          items: d.items || [],
          intent: d.intent || null,
          queryText: text,
          expanded: false,
          followups: d.suggested_followups || [],
        },
      ]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        { from: "bot", text: e.response?.data?.detail || "Sorry, I hit an error. Try again?", items: [] },
      ]);
    } finally {
      setSending(false);
    }
  };

  // Hide on admin pages — staff don't need to be sold to
  // (Admin/staff still get the widget so they can use Admin Ask AI.)

  // -------- Voice input (#12) --------------------------------------------
  const [recState, setRecState] = useState("idle"); // idle | recording | uploading
  const recRef = useRef(null); // { recorder, chunks, stopTimer }

  const startRecording = async () => {
    if (recState !== "idle" || sending) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const recorder = new MediaRecorder(stream, { mimeType: mime });
      const chunks = [];
      recorder.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      recorder.onstop = async () => {
        // Always release the mic
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: mime });
        if (!blob.size) {
          setRecState("idle");
          return;
        }
        setRecState("uploading");
        try {
          const fd = new FormData();
          fd.append("file", blob, "voice.webm");
          const r = await fetch(`${API}/shopper/voice`, {
            method: "POST",
            body: fd,
          });
          if (!r.ok) throw new Error(await r.text());
          const data = await r.json();
          if (data.text) setText((cur) => (cur ? `${cur} ${data.text}` : data.text));
        } catch (err) {
          setMessages((m) => [
            ...m,
            { from: "bot", text: "Sorry, I couldn't catch that — try again?", items: [] },
          ]);
        } finally {
          setRecState("idle");
        }
      };
      recorder.start();
      recRef.current = {
        recorder,
        chunks,
        // auto-stop at 30s as a safety net
        stopTimer: setTimeout(() => {
          try {
            recorder.state === "recording" && recorder.stop();
          } catch {/* ignore */}
        }, 30_000),
      };
      setRecState("recording");
    } catch {
      setMessages((m) => [
        ...m,
        { from: "bot", text: "I need mic access to listen — please enable it in your browser settings.", items: [] },
      ]);
    }
  };

  const stopRecording = () => {
    const ref = recRef.current;
    if (!ref) return;
    clearTimeout(ref.stopTimer);
    try {
      ref.recorder.state === "recording" && ref.recorder.stop();
    } catch {/* ignore */}
    recRef.current = null;
  };

  return (
    <>
      {/* Trigger — desktop only. On mobile the "Atelier" entry in the bottom
          nav opens this same panel via the `luxsova:open-shopper` event, so we
          hide the floating pill to keep the WhatsApp FAB unobstructed. */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="hidden md:inline-flex fixed z-[70] left-4 bottom-6 bg-black text-white rounded-full pl-4 pr-5 py-3 items-center gap-2 shadow-lg hover:bg-neutral-800 transition-colors"
          data-testid="shopper-open"
          aria-label="Open AI Personal Shopper"
        >
          <Sparkles className="w-4 h-4" />
          <span className="text-xs uppercase tracking-[0.16em]">Ask AI</span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="fixed z-[80] left-2 right-2 md:left-4 md:right-auto bottom-2 md:bottom-6 md:w-[400px] md:max-w-[92vw] bg-white border border-black/10 shadow-2xl flex flex-col overflow-hidden"
          style={{ maxHeight: "min(640px, calc(100dvh - 16px))" }}
          data-testid="shopper-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-black text-white">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              <span className="text-xs uppercase tracking-[0.16em]">Luxsova concierge</span>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Close" data-testid="shopper-close" className="p-1">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto overflow-x-hidden px-4 py-4 space-y-4">
            {messages.map((m, i) => (
              <div key={i} data-testid={`shopper-msg-${i}`} className="min-w-0">
                <div
                  className={`text-sm leading-relaxed break-words ${
                    m.from === "user"
                      ? "ml-auto bg-neutral-100 inline-block px-3 py-2 max-w-[80%]"
                      : "text-neutral-800"
                  }`}
                >
                  {m.from === "bot"
                    ? renderRichReply(m.text, () => setOpen(false))
                    : m.text}
                </div>
                {m.from === "bot" && (() => {
                  const actions = extractActions(m.text);
                  if (!actions.length) return null;
                  return (
                    <div
                      className="flex gap-2 flex-wrap mt-2"
                      data-testid={`shopper-actions-${i}`}
                    >
                      {actions.map((p) => (
                        <button
                          key={p}
                          onClick={() => {
                            setOpen(false);
                            navigate(p);
                          }}
                          className="text-[11px] uppercase tracking-[0.16em] bg-black text-white px-3 py-1.5 hover:bg-neutral-800"
                          data-testid={`shopper-cta-${p.replace(/\//g, "")}`}
                        >
                          {ACTION_LABELS[p] || p}
                        </button>
                      ))}
                    </div>
                  );
                })()}
                {m.from === "bot" && m.followups?.length > 0 && (
                  <div
                    className="flex gap-1.5 flex-wrap mt-2"
                    data-testid={`shopper-followups-${i}`}
                  >
                    {m.followups.slice(0, 3).map((q, fi) => (
                      <button
                        key={`fu-${fi}`}
                        onClick={() => send(q)}
                        className="text-[11px] px-2.5 py-1 border border-black/15 rounded-full hover:bg-black hover:text-white hover:border-black transition-colors"
                        data-testid={`shopper-followup-${i}-${fi}`}
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                )}
                {m.items?.length > 0 && (() => {
                  const visible = m.expanded ? m.items : m.items.slice(0, 4);
                  return (
                    <>
                      <div className="grid grid-cols-2 gap-2 mt-3 min-w-0" data-testid={`shopper-results-${i}`}>
                        {visible.map((p) => {
                          const key = `${i}-${p.id}`;
                          const wasAdded = addedKey === key;
                          return (
                            <div key={p.id} className="bg-neutral-50 overflow-hidden group min-w-0">
                              <Link
                                to={`/product/${p.slug}`}
                                className="block"
                                onClick={() => setOpen(false)}
                              >
                                {p.image && (
                                  <div className="aspect-square overflow-hidden">
                                    <img
                                      src={p.image}
                                      alt={p.name}
                                      loading="lazy"
                                      className="w-full h-full object-contain p-2 group-hover:scale-[1.03] transition-transform duration-500"
                                    />
                                  </div>
                                )}
                                <div className="p-2 pb-1">
                                  <p className="text-[11px] truncate">{p.name}</p>
                                  {p.price && (
                                    <p className="text-[11px] font-medium mt-0.5">
                                      ₹{Number(p.price).toLocaleString("en-IN")}
                                    </p>
                                  )}
                                </div>
                              </Link>
                              {!isStaff && (
                                <button
                                  onClick={() => {
                                    cart.addItem(p, 1);
                                    setAddedKey(key);
                                    setTimeout(() => setAddedKey((k) => (k === key ? null : k)), 1800);
                                  }}
                                  className={`w-full text-[10px] uppercase tracking-[0.16em] flex items-center justify-center gap-1 py-1.5 transition-colors ${
                                    wasAdded
                                      ? "bg-emerald-700 text-white"
                                      : "bg-black text-white hover:bg-neutral-800"
                                  }`}
                                  data-testid={`shopper-add-${p.id}`}
                                >
                                  {wasAdded ? (
                                    <>
                                      <Check className="w-3 h-3" /> Added
                                    </>
                                  ) : (
                                    <>
                                      <ShoppingBag className="w-3 h-3" /> Add to bag
                                    </>
                                  )}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {m.items.length > 4 && !m.expanded && (
                        <button
                          onClick={() =>
                            setMessages((list) =>
                              list.map((msg, idx) =>
                                idx === i ? { ...msg, expanded: true } : msg
                              )
                            )
                          }
                          className="mt-3 w-full text-xs uppercase tracking-[0.18em] border border-black/15 hover:bg-neutral-50 py-2.5"
                          data-testid={`shopper-show-more-${i}`}
                        >
                          Show all {m.items.length} results
                        </button>
                      )}
                      {m.items.length > 4 && m.expanded && (
                        <button
                          onClick={() => {
                            const target = buildSearchTarget(
                              m.intent,
                              m.queryText
                            );
                            setOpen(false);
                            navigate(target);
                          }}
                          className="mt-3 w-full text-xs uppercase tracking-[0.18em] bg-black text-white hover:bg-neutral-800 py-2.5"
                          data-testid={`shopper-open-search-${i}`}
                        >
                          {m.intent?.brand ? `See all ${m.intent.brand} →` : "Open full search →"}
                        </button>
                      )}
                    </>
                  );
                })()}
              </div>
            ))}
            {sending && (
              <div className="flex items-center gap-2 text-xs text-neutral-500" data-testid="shopper-thinking">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Thinking…
              </div>
            )}
          </div>

          {/* Suggested chips (only on first turn) */}
          {messages.length === 1 && !sending && (
            <div className="px-4 pb-2 flex gap-2 flex-wrap" data-testid="shopper-suggestions">
              {SUGGESTED.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  className="text-[11px] px-2.5 py-1.5 border border-black/15 hover:bg-neutral-50"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send(text);
            }}
            className="flex items-center gap-2 border-t border-black/10 p-3"
            data-testid="shopper-form"
          >
            <input
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={
                recState === "recording"
                  ? "Listening… tap stop when done"
                  : recState === "uploading"
                  ? "Transcribing…"
                  : "Ask for anything — brand, style, budget…"
              }
              disabled={sending || recState !== "idle"}
              className="flex-1 min-w-0 text-base md:text-sm bg-transparent outline-none disabled:opacity-50"
              data-testid="shopper-input"
            />
            <button
              type="button"
              onClick={recState === "recording" ? stopRecording : startRecording}
              disabled={sending || recState === "uploading"}
              className={`p-2 disabled:opacity-30 ${
                recState === "recording" ? "text-red-600 animate-pulse" : "text-neutral-600"
              }`}
              aria-label={recState === "recording" ? "Stop recording" : "Voice input"}
              data-testid="shopper-mic"
            >
              {recState === "uploading" ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : recState === "recording" ? (
                <Square className="w-4 h-4 fill-current" />
              ) : (
                <Mic className="w-4 h-4" />
              )}
            </button>
            <button
              type="submit"
              disabled={sending || !text.trim() || recState !== "idle"}
              className="p-2 disabled:opacity-30"
              aria-label="Send"
              data-testid="shopper-send"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default PersonalShopper;
